import './App.css';
import React, { useEffect, useState } from 'react';
import Home from './pages/Home';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Header from './components/header_footer/Header';
import { ToastContainer } from "react-toastify";
import OrderSummary from './pages/OrderSummary';
import "react-toastify/dist/ReactToastify.css";
import UserProfile from './pages/UserProfilePage';
import CartPage from './pages/CartPage';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, fetchUserInfo, setLoggedIn, setAuthInitialized } from './store';
import PaymentComponent from './components/payment/payment';
import OrderSuccessPage from './pages/OrderSuccess';
import OrderFailure from './pages/OrderFailure';
import UserManagement from './components/user_management/UserManagement';
import OrderManagement from './components/order_management/OrderManagement';
import ProductManagement from './pages/ProductManagement';
import { observeAuthState } from './firebase/authHelpers';
import { setupAxiosInterceptor } from './utils/axiosInterceptor';
import store from './store';
import { mustCompleteProfileBeforeBrowsing } from './utils/profileCompletion';
import RoleRoute from './components/auth/RoleRoute';

const ROLE_ADMIN = Object.freeze(['admin']);
const ROLE_STAFF = Object.freeze(['oper', 'admin']);
const ROLE_CUSTOMER = Object.freeze(['user']);

function App() {
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    // Set up axios interceptor (only once)
    setupAxiosInterceptor(store);

    // Fetch products immediately
    dispatch(fetchProducts());

    // Set up Firebase auth state observer
    let isInitialLoad = true;
    const unsubscribe = observeAuthState(async (user) => {
      if (user) {
        // User is signed in with Firebase
        dispatch(setLoggedIn(true));

        // Fetch user profile from backend only on initial load
        if (isInitialLoad) {
          try {
            await dispatch(fetchUserInfo()).unwrap();
          } catch (error) {
            console.error('Failed to fetch user info:', error);
          }
        }
      } else {
        // User is signed out
        dispatch(setLoggedIn(false));
      }
      dispatch(setAuthInitialized(true));
      isInitialLoad = false;
    });

    // Cleanup observer on unmount
    return () => unsubscribe();
  }, [dispatch]);

  return (
    <>
      <Router>
        <MainRoutes search={search} setSearch={setSearch} />
      </Router>
      <ToastContainer
        position="top-right"
        style={{ marginTop: "4.0rem" }}
        autoClose={600}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

// Separate component to use useNavigate in Header
function MainRoutes({ search, setSearch }) {
  const navigate = useNavigate();
  const location = useLocation();
  const loggedIn = useSelector((s) => s.auth.loggedIn);
  const user = useSelector((s) => s.user);

  useEffect(() => {
    if (!mustCompleteProfileBeforeBrowsing(loggedIn, user)) return;
    if (location.pathname === '/user-profile') return;
    navigate('/user-profile', { replace: true, state: { profileIncomplete: true } });
  }, [
    loggedIn,
    user.loading,
    user.userId,
    user._id,
    user.firstName,
    user.lastName,
    user.email,
    user.phone,
    user.dob,
    user.role,
    location.pathname,
    navigate,
  ]);

  return (
    <>
      <Header search={search} setSearch={setSearch} setShowCart={() => navigate("/cartPage")} />
      <Routes>
        <Route path="/" element={<Home search={search} />} />
        <Route
          path="/orderdetails"
          element={
            <RoleRoute allowedRoles={ROLE_CUSTOMER}>
              <OrderSummary onClose={() => navigate("/")} />
            </RoleRoute>
          }
        />
        <Route path="/user-profile" element={<UserProfile />} />
        <Route
          path="/cartPage"
          element={<CartPage />}
        />
        <Route
          path="/payment"
          element={
            <RoleRoute allowedRoles={ROLE_CUSTOMER}>
              <PaymentComponent />
            </RoleRoute>
          }
        />
        <Route
          path="/order-success"
          element={
            <RoleRoute allowedRoles={ROLE_CUSTOMER}>
              <OrderSuccessPage />
            </RoleRoute>
          }
        />
        <Route
          path="/order-failed"
          element={
            <RoleRoute allowedRoles={ROLE_CUSTOMER}>
              <OrderFailure />
            </RoleRoute>
          }
        />
        <Route
          path="/users"
          element={
            <RoleRoute allowedRoles={ROLE_ADMIN}>
              <UserManagement />
            </RoleRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <RoleRoute allowedRoles={ROLE_STAFF}>
              <OrderManagement />
            </RoleRoute>
          }
        />
        <Route
          path="/products"
          element={
            <RoleRoute allowedRoles={ROLE_STAFF}>
              <ProductManagement />
            </RoleRoute>
          }
        />
      </Routes>
    </>
  );
}

export default App;